import type { CollectionName } from '../model/schema.js';

/** Catch-up / open-item areas (decision O5), derived from an entity's layer or kind. */
export const TOPICS = [
  'stage',
  'musicians',
  'backline',
  'microphones',
  'monitors',
  'pa',
  'stageboxes',
  'cabling',
  'power',
  'network',
  'lighting',
  'general',
] as const;
export type Topic = (typeof TOPICS)[number];

export type ChangeKind = 'added' | 'changed' | 'removed';

/** Change types of the catch-up filter (decision C3). */
export const CATCHUP_TYPES = ['plot', 'comments', 'openitems'] as const;
export type CatchupType = (typeof CATCHUP_TYPES)[number];

/** Raw entity position (board metres) – for removed ghosts and moved objects. */
export interface EntityPosition {
  x: number;
  y: number;
  rotation: number;
  /** Item width / free-form element w, when known. */
  w?: number;
  /** Item depth / free-form element h, when known. */
  h?: number;
}

/** One changed board entity (or the board meta) between two board states. */
export interface PlotChange {
  type: 'plot';
  /** Stable key, unique within a change list: `<collection>:<id>` or `meta`. */
  key: string;
  collection: CollectionName | 'meta';
  id: string;
  kind: ChangeKind;
  /** Changed top-level fields (`changed` only; empty for added / removed). */
  fields: string[];
  topic: Topic;
  /** Human-readable name (item label, channel "3 Kick", mix name, …); may be empty. */
  label: string;
  /** Position in the old state (removed / changed canvas objects). */
  before?: EntityPosition;
  /** Position in the new state (added / changed canvas objects). */
  after?: EntityPosition;
}

/** Derived tables whose rows a plot change can affect (row keys as in core `derive`). */
export const CATCHUP_TABLES = [
  'input',
  'monitors',
  'patch',
  'power-loads',
  'power-sources',
  'network',
  'stands',
] as const;
export type CatchupTable = (typeof CATCHUP_TABLES)[number];

/** A changed row of a derived table; `fields` = changed row fields (column keys). */
export interface RowChange {
  table: CatchupTable;
  rowKey: string;
  kind: ChangeKind;
  fields: string[];
}

export interface ChangeSet {
  /** Sorted by collection order (meta first), then id. */
  plot: PlotChange[];
  /** In table order, each in the table's row order (removed rows last). */
  rows: RowChange[];
  empty: boolean;
}

/** A member or guest person (guest id = person id, decision C1 revised). */
export interface PersonRef {
  kind: 'member' | 'guest';
  id: string;
}

/** Structural subset of api.CommentAnchor (also used by open items). */
export type AnchorLike =
  | { type: 'item'; itemId: string }
  | { type: 'point'; x: number; y: number }
  | { type: 'row'; table: string; rowKey: string; column?: string }
  | { type: 'board' };

/** Structural subset of api.Comment. */
export interface CommentLike {
  id: string;
  parentId: string | null;
  author: PersonRef & { name: string };
  body: string;
  anchor: AnchorLike;
  createdAt: string;
  editedAt: string | null;
  resolvedAt: string | null;
  /** Mentioned people as identity keys (`member:<userId>` / `guest:<personId>`, see identityKey). */
  mentions: readonly string[];
}

/** Structural subset of an open item (decision O3); adapters map the API DTO onto it. */
export interface OpenItemLike {
  id: string;
  title: string;
  status: string;
  topic: Topic | null;
  assignee: PersonRef | null;
  originator: PersonRef;
  anchor: AnchorLike | null;
  createdAt: string;
  updatedAt: string;
  replies?: readonly {
    id: string;
    author: PersonRef;
    createdAt: string;
    /** Identity keys, as in {@link CommentLike.mentions}. */
    mentions?: readonly string[];
  }[];
  /** Who changed what when (O3); with it, my own edits are not "changes" for me (OIL-186). */
  history?: readonly { actor: PersonRef; createdAt: string }[];
}

/** A new or updated comment thread / open item since the reference time. */
export interface ThreadChange {
  type: 'comments' | 'openitems';
  /** `comment:<root id>` / `openitem:<id>`. */
  key: string;
  /** Comment root id / open item id. */
  id: string;
  kind: 'added' | 'changed';
  topic: Topic;
  anchor: AnchorLike | null;
  /** First line of the comment body / open item title. */
  label: string;
  /** Latest relevant timestamp (ISO). */
  at: string;
}

export type Change = PlotChange | ThreadChange;

/** Who "me" is and what the catch-up relevance is computed from. */
export interface RelevanceContext {
  me: PersonRef;
  comments: readonly CommentLike[];
  openItems: readonly OpenItemLike[];
}

export interface CatchupFilter {
  /** null = all topics. */
  topics: readonly Topic[] | null;
  concernsMe: boolean;
  /** null = all types. */
  types: readonly CatchupType[] | null;
}

export interface CatchupSummary {
  total: number;
  concerningMe: number;
  byType: Record<CatchupType, number>;
}

/** Client-side visit counting (decision C1): a visit counts after 30 s or a first interaction. */
export type VisitPhase = 'idle' | 'pending' | 'counted' | 'ended';

export interface VisitTracker {
  phase: VisitPhase;
  /** Epoch ms of the start (pending / counted / ended). */
  startedAt: number | null;
  /** Epoch ms of the last heartbeat sent (counted). */
  lastBeatAt: number | null;
}

export type VisitEvent =
  | { type: 'start'; at: number }
  | { type: 'tick'; at: number }
  | { type: 'interact'; at: number }
  | { type: 'end'; at: number };

/** Side effects the caller performs: begin = POST visit, heartbeat, end (discard when never counted). */
export type VisitEffect = 'begin' | 'heartbeat' | 'end' | 'discard';

/** Minimal visit record (api.Visit) for history logic. */
export interface VisitLike {
  id: string;
  startedAt: string;
  endedAt: string | null;
  caughtUp: boolean;
}

/** Reference for the comparison (decision C3). */
export type ReferenceSpec =
  | { type: 'last' }
  | { type: 'visit'; id: string }
  | { type: 'date'; at: string }
  | { type: 'snapshot'; id: string; createdAt: string };
